import { generateVideo, pollVideoJob } from "./generateVideo";
import { deleteVideoJob } from "./deleteVideoJob";
import { stitchVideoClips } from "./stitchVideo";
import { unifyStitchedVoice } from "./unifyStitchedVoice";
import { extractLastFrame } from "./extractLastFrame";
import { extractVoiceSnippet } from "./extractVoiceSnippet";
import { runServerOrchestratedGeneration } from "./orchestrateLongVideo";
import { mediaSrc } from "../../../utils/mediaSrc";
import {
  buildKlingSegmentForm,
  needsAutoStitch,
  needsKlingStitch,
} from "../klingSegmentPlan";
import { sceneDurationsFromForm } from "../scenePlan";
import { resolveStitchVoiceLock } from "../klingVoiceDefaults";
import { modelSupportsKlingVoiceover } from "../modelProfiles";
import { POYO_MODELS } from "../categories";
import { estimateCreditsFromPricing, fetchVideoPricing } from "./fetchVideoPricing";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function isSegmentJobReady(job) {
  return (
    Boolean(job?.video_url) &&
    (job.status === "segment_ready" || job.status === "completed")
  );
}

async function pollUntilComplete(hitAxios, jobId, maxAttempts = 180) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const job = await pollVideoJob(hitAxios, jobId);
    if (isSegmentJobReady(job)) {
      return job;
    }
    if (job.status === "failed") {
      throw new Error(job.error_message || "Segment generation failed.");
    }
    await sleep(attempt < 5 ? 4000 : 5500);
  }
  throw new Error("Segment generation timed out.");
}

async function deleteSegmentJobs(hitAxios, boardClips) {
  const ids = [...new Set(boardClips.map((c) => c.jobId).filter((id) => id != null))];
  await Promise.allSettled(
    ids.map((id) =>
      deleteVideoJob(hitAxios, id).catch(() => {
        /* segment rows are best-effort cleanup */
      }),
    ),
  );
}

function modelLabelFor(form) {
  return POYO_MODELS[form.model]?.label || form.model || "Video";
}

/**
 * Generate one scene (0-based index). Safe to call in parallel for different indices
 * when each scene has its own start image, or when continuity is optional.
 *
 * @param {Function} hitAxios
 * @param {object} form
 * @param {string} categoryId
 * @param {{
 *   segmentIndex0: number;
 *   chainVideoUrl?: string|null;
 *   voiceLock?: object|null;
 *   creditsBalance?: number|string;
 * }} opts
 */
export async function generateSingleSegment(hitAxios, form, categoryId, opts = {}) {
  const segments = sceneDurationsFromForm(form);
  const total = segments.length;
  const i = Math.max(0, Math.min(total - 1, Math.round(Number(opts.segmentIndex0) || 0)));
  const modelLabel = modelLabelFor(form);
  const klingVoice = modelSupportsKlingVoiceover(form.model);
  const voiceLock =
    opts.voiceLock !== undefined
      ? opts.voiceLock
      : klingVoice
        ? resolveStitchVoiceLock(form)
        : null;

  const pricing = await fetchVideoPricing(hitAxios);
  const genCredits = estimateCreditsFromPricing(segments[i], pricing, form.model) || 0;
  const balance =
    opts.creditsBalance != null
      ? Math.max(0, parseInt(String(opts.creditsBalance), 10) || 0)
      : null;
  if (balance != null && genCredits > 0 && balance < genCredits) {
    throw new Error(
      `Insufficient credits — needs ${genCredits} for scene ${i + 1}, has ${balance}.`,
    );
  }

  let chainFrameUrl;
  let chainAudioUrl;
  const chainVideoUrl = opts.chainVideoUrl || null;
  if (chainVideoUrl) {
    try {
      chainFrameUrl = await extractLastFrame(hitAxios, chainVideoUrl);
    } catch {
      chainFrameUrl = undefined;
    }
    if (klingVoice && form.sound !== false && voiceLock) {
      try {
        chainAudioUrl = await extractVoiceSnippet(hitAxios, chainVideoUrl);
      } catch {
        chainAudioUrl = null;
      }
    }
  }

  const segmentForm = buildKlingSegmentForm(form, {
    segmentIndex: i,
    totalSegments: total,
    segmentDuration: segments[i],
    segmentDurations: segments,
    chainFrameUrl,
    voiceLock,
    chainAudioUrl: i > 0 ? chainAudioUrl || undefined : undefined,
  });

  const segmentTitle =
    total > 1
      ? `${String(form.title || "Video").trim()} — part ${i + 1}/${total}`
      : String(form.title || "").trim();

  const { jobId } = await generateVideo(
    hitAxios,
    { ...segmentForm, title: segmentTitle },
    categoryId,
    {
      segmentOnly: true,
      segmentIndex: i + 1,
      segmentTotal: total,
    },
  );

  const job = await pollUntilComplete(hitAxios, jobId);
  const videoUrl = mediaSrc(job.video_url) || job.video_url;
  const clip = {
    url: videoUrl,
    duration: segments[i],
    modelId: form.model,
    modelLabel,
    aspectRatio: form.aspectRatio || null,
    outputResolution: form.resolution || null,
    jobId: job.id,
    thumbnailUrl: videoUrl,
    segmentIndex: i + 1,
    segmentTotal: total,
    status: "ready",
  };

  return { clip, job, segmentIndex0: i, total, segments, voiceLock };
}

/**
 * Join ready segment clips into one video (optional voice unify).
 * Keeps clip board URLs — only clears temporary segment job rows.
 */
export async function joinReadySceneClips(hitAxios, form, boardClips, opts = {}) {
  const ordered = [...(boardClips || [])]
    .filter((c) => c?.url && c.status !== "failed" && Number(c.segmentIndex) >= 1)
    .sort((a, b) => Number(a.segmentIndex) - Number(b.segmentIndex));

  if (ordered.length < 2) {
    throw new Error("Need at least 2 ready scenes to join.");
  }

  const klingVoice = modelSupportsKlingVoiceover(form.model);
  const voiceLock =
    opts.voiceLock !== undefined
      ? opts.voiceLock
      : klingVoice
        ? resolveStitchVoiceLock(form)
        : null;

  opts.onProgress?.({ phase: "stitching", total: ordered.length });

  const stitchData = await stitchVideoClips(hitAxios, {
    clips: ordered.map((c) => ({ url: c.url, duration: c.duration })),
    output_filename: `${String(form.title || "video").trim().replace(/[^\w.-]+/g, "_") || "video"}_stitched.mp4`,
    output_resolution: "source",
  });

  let finalVideoUrl = mediaSrc(stitchData.output_url) || stitchData.output_url;
  let finalJobId = stitchData.job_id || null;

  const shouldUnifyVoice =
    !form.multiSpeakerDialogue &&
    klingVoice &&
    form.sound !== false &&
    voiceLock &&
    (voiceLock.mode === "ai" ||
      voiceLock.mode === "upload-seed" ||
      (voiceLock.mode === "preset" && voiceLock.id));

  let segment1VoiceUrl = opts.segment1VoiceUrl || null;
  if (shouldUnifyVoice && !segment1VoiceUrl) {
    const first = ordered.find((c) => Number(c.segmentIndex) === 1);
    if (first?.url) {
      try {
        segment1VoiceUrl = await extractVoiceSnippet(hitAxios, first.url);
      } catch {
        segment1VoiceUrl = null;
      }
    }
  }

  if (shouldUnifyVoice) {
    opts.onProgress?.({ phase: "voiceover", total: ordered.length });
    const uploadSeedUrl =
      voiceLock.mode === "upload-seed"
        ? (form.referenceAudioUploads || []).find((u) => u?.previewUrl)?.previewUrl
        : undefined;
    try {
      const voiced = await unifyStitchedVoice(hitAxios, {
        video_url: stitchData.stored || stitchData.output_url || finalVideoUrl,
        voice_id: voiceLock.mode === "preset" ? voiceLock.id : undefined,
        seed_audio_url: uploadSeedUrl || segment1VoiceUrl || undefined,
        job_id: finalJobId || undefined,
      });
      finalVideoUrl = mediaSrc(voiced.output_url) || voiced.output_url;
      finalJobId = voiced.job_id || finalJobId;
    } catch (voErr) {
      throw new Error(voErr?.message || "Could not unify voice on stitched video.");
    }
  }

  if (opts.deleteSegmentJobs !== false) {
    await deleteSegmentJobs(hitAxios, ordered);
  }

  return {
    videoUrl: finalVideoUrl,
    jobId: finalJobId,
    segmentCount: ordered.length,
    segmentDurations: ordered.map((c) => c.duration),
    stitchData,
  };
}

/**
 * Generate a video longer than one API clip via scenes + stitch.
 *
 * @param {Function} hitAxios
 * @param {object} form
 * @param {string} categoryId
 * @param {{
 *   onProgress?: (info: { phase: string; segment?: number; total?: number }) => void;
 *   onSegmentReady?: (info: { segment: number; total: number; clip: object; job: object }) => void;
 *   onSegmentFailed?: (info: { segment: number; total: number; error: string; clip: object }) => void;
 *   onVoiceLocked?: (info: { id: string; label: string; autoLocked?: boolean }) => void;
 *   onSegmentStart?: (info: { segment: number; total: number }) => void;
 *   clearBoard?: () => void;
 *   creditsBalance?: number|string;
 *   startIndex?: number;
 *   seedClips?: object[];
 *   skipReady?: boolean;
 *   onlySegmentIndex0?: number;
 *   autoJoin?: boolean;
 *   isSegmentBusy?: (segment1Based: number) => boolean;
 * }} [opts]
 */
export async function runAutoStitchedGeneration(hitAxios, form, categoryId, opts = {}) {
  const segments = sceneDurationsFromForm(form);
  const total = segments.length;
  const startIndex = Math.max(0, Math.min(total - 1, Math.round(Number(opts.startIndex) || 0)));
  const only =
    opts.onlySegmentIndex0 != null
      ? Math.max(0, Math.min(total - 1, Math.round(Number(opts.onlySegmentIndex0))))
      : null;
  const autoJoin = opts.autoJoin !== false && only == null;

  // Full multi-segment runs go through the durable server orchestrator
  // (plan → generate → last-frame continue → stitch → optional voice).
  const useServerOrchestrator =
    autoJoin &&
    only == null &&
    startIndex === 0 &&
    (!Array.isArray(opts.seedClips) || opts.seedClips.length === 0) &&
    total >= 2;

  if (useServerOrchestrator) {
    try {
      return await runServerOrchestratedGeneration(hitAxios, form, categoryId, opts);
    } catch (err) {
      // Fall back to client orchestration if the server endpoint is unavailable.
      const msg = String(err?.message || "");
      if (!/orchestrat|404|Not Found|table is missing/i.test(msg)) {
        throw err;
      }
      console.warn("[video-studio] server orchestrator unavailable, using client fallback:", msg);
    }
  }

  const modelLabel = modelLabelFor(form);
  const klingVoice = modelSupportsKlingVoiceover(form.model);
  const voiceLock = klingVoice ? resolveStitchVoiceLock(form) : null;

  const seedClips = (Array.isArray(opts.seedClips) ? opts.seedClips : [])
    .filter(
      (c) =>
        c?.url &&
        c.status !== "failed" &&
        Number(c.segmentIndex) >= 1 &&
        Number(c.segmentIndex) <= total,
    )
    .sort((a, b) => Number(a.segmentIndex) - Number(b.segmentIndex));

  if (voiceLock?.mode === "preset" && voiceLock.id) {
    opts.onVoiceLocked?.({
      id: voiceLock.id,
      label: voiceLock.label || voiceLock.id,
      autoLocked: false,
    });
  }

  const indices =
    only != null
      ? [only]
      : Array.from({ length: total - startIndex }, (_, k) => startIndex + k);

  const remainingDurations = indices.map((i) => segments[i]);
  const pricing = await fetchVideoPricing(hitAxios);
  const genCredits = remainingDurations.reduce(
    (sum, d) => sum + (estimateCreditsFromPricing(d, pricing, form.model) || 0),
    0,
  );
  const stitchFlat = autoJoin ? Number(pricing?.stitching_flat) || 0 : 0;
  const totalNeeded = genCredits + stitchFlat;
  const balance =
    opts.creditsBalance != null
      ? Math.max(0, parseInt(String(opts.creditsBalance), 10) || 0)
      : null;
  if (balance != null && totalNeeded > 0 && balance < totalNeeded) {
    const remaining = remainingDurations.length;
    throw new Error(
      `Insufficient credits — needs ${totalNeeded} for ${remaining} segment${remaining === 1 ? "" : "s"}` +
        (stitchFlat > 0 ? ` + stitch` : "") +
        `, has ${balance}.`,
    );
  }

  if (startIndex === 0 && only == null) {
    opts.clearBoard?.();
  }

  const boardBySeg = new Map();
  for (const c of seedClips) {
    boardBySeg.set(Number(c.segmentIndex), {
      url: c.url,
      duration: c.duration || segments[Number(c.segmentIndex) - 1] || 5,
      modelId: c.modelId || form.model,
      modelLabel: c.modelLabel || modelLabel,
      aspectRatio: c.aspectRatio || form.aspectRatio || null,
      outputResolution: c.outputResolution || form.resolution || null,
      jobId: c.jobId ?? null,
      thumbnailUrl: c.thumbnailUrl || c.url,
      segmentIndex: Number(c.segmentIndex),
      segmentTotal: total,
      status: "ready",
    });
  }

  let segment1VoiceUrl = null;

  for (const i of indices) {
    const seg1 = i + 1;

    if (typeof opts.isSegmentBusy === "function" && opts.isSegmentBusy(seg1)) {
      for (let w = 0; w < 120; w += 1) {
        await sleep(2500);
        if (!opts.isSegmentBusy(seg1)) break;
      }
    }

    const externalReady =
      typeof opts.getReadyClip === "function" ? opts.getReadyClip(seg1) : null;
    if (externalReady?.url) {
      boardBySeg.set(seg1, {
        ...externalReady,
        segmentIndex: seg1,
        segmentTotal: total,
        status: "ready",
      });
      continue;
    }

    if (opts.skipReady && boardBySeg.get(seg1)?.status === "ready" && boardBySeg.get(seg1)?.url) {
      continue;
    }

    opts.onSegmentStart?.({ segment: seg1, total });
    opts.onProgress?.({ phase: "generating", segment: seg1, total });

    // Previous scene is 1-based index `i` when current is 0-based `i`.
    // Multi-speaker dialogue: each turn has its own character image — never chain faces.
    const chainClip =
      form.multiSpeakerDialogue || (form.scenes?.[i]?.imageUrlUploads || []).length
        ? null
        : i > 0
          ? boardBySeg.get(i)
          : null;

    try {
      const { clip, job } = await generateSingleSegment(hitAxios, form, categoryId, {
        segmentIndex0: i,
        chainVideoUrl: chainClip?.url || null,
        voiceLock: form.multiSpeakerDialogue ? null : voiceLock,
        creditsBalance: undefined,
      });

      boardBySeg.set(seg1, clip);
      opts.onSegmentReady?.({ segment: seg1, total, clip, job });

      if (i === 0 && klingVoice && form.sound !== false && voiceLock) {
        try {
          segment1VoiceUrl = await extractVoiceSnippet(hitAxios, clip.url);
        } catch {
          segment1VoiceUrl = null;
        }
      }
    } catch (err) {
      const message = err?.message || "Segment generation failed.";
      const failedClip = {
        url: "",
        duration: segments[i],
        modelId: form.model,
        modelLabel,
        aspectRatio: form.aspectRatio || null,
        outputResolution: form.resolution || null,
        jobId: null,
        thumbnailUrl: null,
        segmentIndex: seg1,
        segmentTotal: total,
        status: "failed",
        errorMessage: message,
      };
      opts.onSegmentFailed?.({
        segment: seg1,
        total,
        error: message,
        clip: failedClip,
      });
      throw err instanceof Error ? err : new Error(message);
    }
  }

  const boardClips = [...boardBySeg.values()].sort(
    (a, b) => Number(a.segmentIndex) - Number(b.segmentIndex),
  );

  if (!autoJoin) {
    return {
      videoUrl: null,
      jobId: null,
      segmentCount: total,
      segmentDurations: segments,
      boardClips,
      skippedJoin: true,
    };
  }

  if (!segment1VoiceUrl && klingVoice && form.sound !== false && voiceLock) {
    const first = boardClips.find((c) => c.segmentIndex === 1 && c.url);
    if (first?.url) {
      try {
        segment1VoiceUrl = await extractVoiceSnippet(hitAxios, first.url);
      } catch {
        segment1VoiceUrl = null;
      }
    }
  }

  const joined = await joinReadySceneClips(hitAxios, form, boardClips, {
    voiceLock,
    segment1VoiceUrl,
    onProgress: opts.onProgress,
  });

  return {
    ...joined,
    segmentCount: total,
    segmentDurations: segments,
    boardClips,
  };
}

/** @deprecated Prefer runAutoStitchedGeneration */
export const runKlingStitchedGeneration = runAutoStitchedGeneration;

export { needsAutoStitch, needsKlingStitch };
